
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMechanicShare, ShareLink } from '@/contexts/MechanicShareContext';
import { Car } from '@/contexts/CarContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Copy, Trash2, LinkIcon, Clock, Eye, Pencil } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ShareLinkManagerProps {
  car: Car;
}

interface FormValues {
  isEditable: boolean;
  expirationHours: string;
}

const ShareLinkManager: React.FC<ShareLinkManagerProps> = ({ car }) => {
  const { shareLinks, createShareLink, deleteShareLink, isLinkValid } = useMechanicShare();
  const [showForm, setShowForm] = useState(false);
  
  // Filter share links for this car only
  const carShareLinks = shareLinks.filter(link => link.carId === car.id);
  
  const form = useForm<FormValues>({
    defaultValues: {
      isEditable: true,
      expirationHours: '24',
    }
  });

  const onSubmit = (values: FormValues) => {
    const link = createShareLink(
      car.id, 
      values.isEditable, 
      parseInt(values.expirationHours)
    );
    
    // Generate the full URL
    const fullUrl = `${window.location.origin}/mechanic-share?token=${link.token}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(fullUrl)
      .then(() => {
        toast.success('Link copied to clipboard');
        setShowForm(false);
      })
      .catch(() => {
        toast.error('Failed to copy link');
      });
  };

  const copyLink = (token: string) => {
    const fullUrl = `${window.location.origin}/mechanic-share?token=${token}`;
    navigator.clipboard.writeText(fullUrl)
      .then(() => toast.success('Link copied to clipboard'))
      .catch(() => toast.error('Failed to copy link'));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Mechanic Share Links</h3>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Create New Link'}
        </Button>
      </div>
      
      {showForm && (
        <Card className="border-lime/30">
          <CardHeader>
            <CardTitle className="text-lg">Create Mechanic Share Link</CardTitle>
            <CardDescription>
              Generate a secure link to share with your mechanic
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="isEditable"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Allow editing</FormLabel>
                        <FormDescription className="text-sm text-muted-foreground">
                          Let the mechanic add repairs and symptoms
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="expirationHours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Link expires after</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="24">24 hours</SelectItem>
                          <SelectItem value="48">48 hours</SelectItem>
                          <SelectItem value="72">3 days</SelectItem>
                          <SelectItem value="168">7 days</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full mt-2 bg-lime hover:bg-lime/90 text-black"
                >
                  Generate & Copy Link
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}
      
      {carShareLinks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No share links created yet
        </div>
      ) : (
        <div className="space-y-3">
          {carShareLinks.map((link) => {
            const isValid = isLinkValid(link.token);
            const expiresAt = new Date(link.expiresAt);
            
            return (
              <div 
                key={link.id} 
                className={`border rounded-lg p-3 ${
                  !isValid ? 'opacity-60 bg-gray-50 dark:bg-gray-800/50' : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-2">
                    <LinkIcon size={16} className="text-gray-500" />
                    <span className="font-medium truncate max-w-[150px] sm:max-w-xs">
                      {link.token.substring(0, 8)}...
                    </span>
                  </div>
                  
                  <div className="flex space-x-1">
                    {isValid ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-gray-100 text-gray-500">
                        Expired
                      </Badge>
                    )}
                    
                    {link.isEditable ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                        <Pencil size={12} className="mr-1" />
                        Edit
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                        <Eye size={12} className="mr-1" />
                        View
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="mt-2 flex justify-between items-center text-xs text-gray-500">
                  <div className="flex items-center">
                    <Clock size={14} className="mr-1" />
                    {isValid 
                      ? `Expires ${formatDistanceToNow(expiresAt, { addSuffix: true })}`
                      : `Expired ${formatDistanceToNow(expiresAt, { addSuffix: true })}`
                    }
                  </div>
                  
                  <div className="flex space-x-1">
                    {isValid && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 px-2 text-gray-500" 
                        onClick={() => copyLink(link.token)}
                      >
                        <Copy size={14} />
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50" 
                      onClick={() => {
                        deleteShareLink(link.id);
                        toast.success('Link deleted');
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShareLinkManager;
